<!-- @format -->

<template>
    <div>
        <ManageBookingCheckoutLayout :billing-details="booking.billingDetails">
            <template #left>
                <BookingDetailsSummary
                    :booking-hotel-room="bookingHotelRoom"
                    :booking-hotel="bookingHotel"
                    class="d-flex d-md-none" />
            </template>
            <template #right>
                <BookingPaymentSummary :total-amount="totalAmountFormatted">
                    <template #action>
                        <BaseButton
                            color="primary"
                            data-test-id="button.refund"
                            class="gst-manage-booking-action__button u-width-100 mb-4 mt-6"
                            @click="onClickRefundDo">
                            {{ $t('buttons.refund') }}
                        </BaseButton>
                    </template>
                </BookingPaymentSummary>
                <BookingDetailsSummary
                    :booking-hotel-room="bookingHotelRoom"
                    :booking-hotel="bookingHotel"
                    class="d-none d-md-flex" />
            </template>
        </ManageBookingCheckoutLayout>
    </div>
</template>
<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import LogService from '@core/services/LogService';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import ManageBookingCheckoutLayout from './_components/ManageBookingCheckoutLayout';
import BookingDetailsSummary from './_components/BookingDetailsSummary';
import BookingPaymentSummary from './_components/BookingPaymentSummary';

export default {
    name: 'ManageBookingCheckoutRefund',
    components: {
        ManageBookingCheckoutLayout,
        BookingPaymentSummary,
        BookingDetailsSummary,
    },

    mixins: [EventFlowMixin],
    props: {
        booking: {
            type: Object,
            default: () => ({}),
        },
        bookingHotel: {
            type: Object,
            default: () => ({}),
        },
        bookingHotelRoom: {
            type: Object,
            default: () => ({}),
        },
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel.features.manageBooking.theManageBookingCheckout',
    },
    data() {
        return {
            busEventProgressCircular: new Vue(),
        };
    },
    computed: {
        ...mapGetters({
            bookingCartId: 'booking/getCartId',
        }),
        computedCartId() {
            return this.bookingCartId || this.$route.params.id;
        },
        totalAmountFormatted() {
            return this.$options.filters.currencyFilter(
                this.booking.totalAmount,
                this.booking.currency
            );
        },
        orderUuid() {
            return this.booking.uuid;
        },
    },
    methods: {
        ...mapActions({
            requestRefund: 'cart/requestRefund',
            setBookingNeedsUpdate: 'booking/setNeedsUpdate',
        }),
        async onClickRefundDo() {
            let response;
            const componentProps = {
                title: this.$t('_components.confirmationModal.refund.title'),
                content: this.$t('_components.confirmationModal.refund.content'),
                confirmButton: this.$t('_components.confirmationModal.refund.confirm'),
                cancelButton: this.$t('_components.confirmationModal.refund.cancel').toUpperCase(),
                showCloseIcon: true,
                onConfirmFn: () => {
                    this.$modal.show(
                        import('@tenant/app/components/modals/ProgressCircularAnimationModal.vue'),
                        {
                            title: this.$t('_components.progressCircularAnimationModal.title'),
                            busEvent: this.busEventProgressCircular,
                            variant: 1,
                        },
                        {
                            'no-click-animation': true,
                            persistent: true,
                            scrollable: false,
                            fullscreen: false,
                            'hide-overlay': false,
                            'max-width': 700,
                        },
                        {
                            open: async () => {
                                response = await this.requestRefund({
                                    orderId: this.orderUuid,
                                });
                                if (response.success) {
                                    this.busEventProgressCircular.$emit('end');
                                } else {
                                    this.busEventProgressCircular.$emit('close');
                                }
                            },
                            beforeClose: () => {
                                if (response?.success) {
                                    this.setBookingNeedsUpdate(true);
                                    this.$router.replace({
                                        name: 'booking',
                                        params: { id: this.bookingCartId },
                                    });
                                } else {
                                    LogService.log('Unable to request a refund');
                                }
                            },
                        }
                    );
                },
                onCancelFn: () => {
                    if (this.$router.history._startLocation.includes('checkout'))
                        this.$router.push({ name: 'booking', params: { id: this.computedCartId } });
                    else this.$router.back();
                },
                onCloseFn: () => {},
            };

            this.$modal.show(
                () => import('@tenant/app/components/modals/ConfirmationModal'),
                {
                    ...componentProps,
                },
                {
                    'max-width': 858,
                    persistent: true,
                }
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@scssMixins';
@import '@scssVariables';

.gst-manage-booking-action__button {
    font-size: font-size('l');
    font-weight: font-weight('large');
}
</style>
